import { useQuery } from "@tanstack/react-query";
import {
  AccountMemberDetails,
  PrimaryUser,
  SetupContextDetails,
  UserDetails,
  UserFields,
} from "features/types";
import { get, post, put } from "services/http";
export interface CreateContactParams {
  source:
    | "REGISTRATION"
    | "GROUP_INVITE"
    | "SHOP_PAGE_PLAN_PICKER"
    | "SHOP_PAGE_PHASE_TWO"
    | "OTHER";
  email: string;
  firstName?: string;
  lastName?: string;
  phone?: string;
  planPackageId?: number;
  buyflowStep?: string;
  currentUrl?: string;
}

const getCurrentUrl = () =>
  typeof window !== "undefined" ? window.location.href : undefined;

export const createContactFetcher = async (params: CreateContactParams) => {
  return post({
    url: "/users/contacts",
    params: {
      ...params,
      currentUrl: params.currentUrl ?? getCurrentUrl(),
    },
  });
};

export const createUserFetcher = async ({
  user,
  userFields,
}: {
  user: PrimaryUser;
  userFields: UserFields;
}) => {
  return post({
    url: `/users/register`,
    params: {
      ...user,
      userFields,
    },
  }) as Promise<UserDetails>;
};

export const updateUserFetcher = ({
  userId,
  user,
}: {
  userId: number;
  user: UserDetails;
}) => {
  return put({
    url: `/users/${userId}`,
    params: user,
  }) as Promise<UserDetails>;
};

export const useFetchCurrentUserQuery = (userId?: number) => {
  return useQuery(
    ["fetchCurrentUser", userId],
    () => {
      return get({
        url: "/users",
      }) as Promise<UserDetails>;
    },
    {
      staleTime: Infinity,
      enabled: !!userId,
    }
  );
};
export const initiatePasswordResetFetcher = (username: string) => {
  return post({
    url: "/users/initiatePasswordReset",
    params: {
      username,
    },
  });
};

export const resetPasswordFetcher = (params: {
  username: string;
  token: string;
  password: string;
  confirmPassword: string;
}) => {
  return post({
    url: "/users/passwordReset",
    params,
  });
};

export const setupContextFetcher = (params: {
  username: string;
  token: string;
}) => {
  return post({
    url: "/users/setupContext",
    params,
  }) as Promise<SetupContextDetails>;
};

export const useFetchUserMembershipsQuery = () => {
  // endpoint for native app to retreive list of user memberships
  return useQuery(
    ["fetchUserMemberships"],
    () => {
      return get({
        url: "/users/memberships",
      }) as Promise<AccountMemberDetails[]>;
    },
    {
      staleTime: Infinity,
    }
  );
};

export const checkPhoneVerifiedRequest = (params: { phone: string }) => {
  return post({
    url: "/users/check_phone_verified",
    params,
  }) as Promise<{ value: boolean }>;
};

export const initiatePhoneVerifyRequest = (params: { username: string }) => {
  return post({
    url: "/users/initiate_phone_verify",
    params,
  });
};

export const verifyPhoneRequest = (params: {
  username: string;
  token: string;
  userId: number;
}) => {
  return post({
    url: "/users/phoneVerify",
    params,
  }) as Promise<UserDetails>;
};
